import { useEffect, useMemo, useState } from "react";
import {
  OrderInterface,
  StoreInterface,
  AccountInformation,
  CountryInterface,
  CURRENCIES,
  WhatsappCheckoutOption,
  CheckoutChannels,
} from "../../../assets/interfaces";
import { parseJwtTokens, shuffleArray } from "../../../assets/js/utils/functions";
import getWhatsappLink, { getInstagramDMLink, getOrderMessage } from "../../../assets/js/utils/get-whatsapp-order-link";
import { resolvePhone, sendFacebookEvent } from "../../../assets/js/utils/utils";
import { useModals } from "../../hooks/useModals";
import Portal from "../../portal";
import { AppBtn } from "../../ui/buttons";
import { CardWithDetails } from "../../ui/card-with-details";
import ErrorLabel from "../../ui/error-label";
import Radio from "../../ui/form-elements/radio";
import { ModalBody, ModalFooter } from "../../ui/modal";
import { CurrencyData, FormatCurrencyFun } from "../../../contexts/cart-context";
import useCopyClipboard from "@/components/hooks/useCopyClipboard";

interface Props {
  store: StoreInterface;
  order: OrderInterface;
  nextStep: VoidFunction;
  prevStep: VoidFunction;
  checkoutOption: WhatsappCheckoutOption;
  setCheckoutOption: (c: WhatsappCheckoutOption) => void;
  changeStep: (step: string) => void;
  account?: AccountInformation;
  currencies: CurrencyData;
  formatAsCurrency: FormatCurrencyFun;
}

const FinalizeOrder: React.FC<Props> = ({
  store,
  order,
  nextStep,
  prevStep,
  checkoutOption,
  setCheckoutOption,
  changeStep,
  account,
  currencies,
  formatAsCurrency,
}) => {
  const [error, setError] = useState<string>(null);
  const [orderCompletionMethod, setOrderCompletionMethod] = useState<
    "whatsapp" | "pay-now" | "instagram" | "order-page"
  >(null);
  const { modals, toggleModal } = useModals(["summary"]);
  // const showPayments = useMemo(() => {
  //   const country = (store?.country as CountryInterface)?.code;
  //   return ;
  //   // return (
  //   //   (country === "GH" && currencies.active === CURRENCIES.GHC) ||
  //   //   (country === "NG" && currencies.active === CURRENCIES.NGN) ||
  //   //   (country === "KE" && currencies.active === CURRENCIES.KES) ||
  //   //   (country === "ZA" && currencies.active === CURRENCIES.ZAR)
  //   // );
  // }, [store?.country, currencies.active]);

  

  const showPayments = store?.payment_options?.[currencies?.active]?.length > 0 || store?.payment_options?.length > 0;

  const [copied, copy] = useCopyClipboard("", {
    successDuration: 500,
    successTitle: "Order copied to clipboard",
    successMessage: "Click complete order and paste in Merchant's DM",
  });
  const [allowProceed, setAllowProceed] = useState(true); //used to delay the user after copying from instagram

  const { whatsapp, instagram } = store?.checkout_channels;
  const showInstagramOption = instagram?.enabled && instagram?.username && instagram?.username.trim() !== "";

  const whatsappOptions = useMemo(() => shuffleArray<WhatsappCheckoutOption>(whatsapp), []);
  const payementsEnabled = store?.payments_enabled && store?.configuration?.direct_checkout_enabled && order?.invoice;

  const isStoreOwner = useMemo(() => {
    if (!window.localStorage.token) return false;
    const parsedToken = parseJwtTokens(window.localStorage.token);

    return parsedToken?.store?.id === (order?.store as StoreInterface)?.id;
  }, []);

  const disableWhatsappCompletion = !store?.configuration?.whatsapp_checkout_enabled && store?.payments_enabled;
  useEffect(() => {
    if (!payementsEnabled || !showPayments) {
      setOrderCompletionMethod("whatsapp");
    }
  }, [payementsEnabled, showPayments]);

  useEffect(() => {
    if (orderCompletionMethod === "order-page") {
      window.open(`/orders/${order?.id}`, "_blank");
    }
  }, [orderCompletionMethod]);

  useEffect(() => {
    setCheckoutOption(whatsapp[0]);
  }, []);

  const submitIsEnabled = () => {
    return (
      orderCompletionMethod === "pay-now" ||
      (orderCompletionMethod === "whatsapp" && checkoutOption !== null && !disableWhatsappCompletion) ||
      (orderCompletionMethod === "instagram" && allowProceed)
    );
  };

  const trackPurchaseWithFacebook = () =>
    sendFacebookEvent("Purchase", {
      value: order?.total_amount,
      currency: order?.currency,
      content_ids: order?.items.map((i) => i.id),
      num_items: order?.items?.length,
      contents: order.items.map((item) => ({ id: item.id, quantity: item.quantity, item_price: item.item.price })),
    });

  const complete = () => {
    switch (orderCompletionMethod) {
      case "pay-now":
        nextStep();
        break;
      case "whatsapp":
        if (!checkoutOption) {
          setError("Please select a number to checkout to");
          return;
        }

        window
          .open(
            getWhatsappLink(order, "Hi, i'll like to buy these items:", checkoutOption.phone, account, null),
            "_blank"
          )
          .focus();

        trackPurchaseWithFacebook();
        changeStep("success");
        break;
      case "instagram":
        setAllowProceed(false);
        window.open(getInstagramDMLink(instagram?.username), "_blank").focus();
        setTimeout(() => {
          setAllowProceed(true);
        }, 1000); //delay the user from clicking the proceed button

        trackPurchaseWithFacebook();
        changeStep("success");
        break;
      default:
      //do nothing
    }
  };

  return (
    <>
      <ModalBody className="relative">
        {store.configuration.payment_validates_order && (
          <div className="bg-white sticky top-0 z-[1000] -mx-5 sm:-mx-6.25 transform -translate-y-5 sm:-translate-y-6.25 shadow-card !mb-0">
            <div className="py-2.5 text-black-secondary bg-accent-yellow-500 bg-opacity-20 px-4">
              <div className="flex items-start text-1xs sm:text-sm">
                <figure className="h-6 w-6 shadow-card bg-white rounded-full mr-2.5 flex items-center justify-center flex-shrink-0">
                  {/* prettier-ignore */}
                  <svg width="75%" viewBox="0 0 24 24" fill="none" className="text-accent-yellow-500">
                  <path d="M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C17.51 22 22 17.51 22 12C22 6.49 17.51 2 12 2ZM11.25 8C11.25 7.59 11.59 7.25 12 7.25C12.41 7.25 12.75 7.59 12.75 8V13C12.75 13.41 12.41 13.75 12 13.75C11.59 13.75 11.25 13.41 11.25 13V8ZM12.92 16.38C12.87 16.51 12.8 16.61 12.71 16.71C12.61 16.8 12.5 16.87 12.38 16.92C12.26 16.97 12.13 17 12 17C11.87 17 11.74 16.97 11.62 16.92C11.5 16.87 11.39 16.8 11.29 16.71C11.2 16.61 11.13 16.51 11.08 16.38C11.03 16.26 11 16.13 11 16C11 15.87 11.03 15.74 11.08 15.62C11.13 15.5 11.2 15.39 11.29 15.29C11.39 15.2 11.5 15.13 11.62 15.08C11.86 14.98 12.14 14.98 12.38 15.08C12.5 15.13 12.61 15.2 12.71 15.29C12.8 15.39 12.87 15.5 12.92 15.62C12.97 15.74 13 15.87 13 16C13 16.13 12.97 16.26 12.92 16.38Z" fill="currentColor"/>
                </svg>
                </figure>
                Please make payment within {store?.configuration?.payment_timeout ?? "20"} minutes to avoid order being
                cancelled
              </div>
            </div>
          </div>
        )}
        <ErrorLabel error={error} setError={setError} />
        <div className="flex flex-col items-center relative">
          <div className="flex items-center flex-col mb-2.5">
            <div className="w-16.25 h-16.25 sm:h-17.5 sm:w-17.5 md:h-20 md:w-20 rounded-full bg-success flex items-center justify-center text-white">
              {
                // prettier-ignore
                <svg width="50%" viewBox="0 0 24 24" fill="none">
                  <path d="M19.24 5.57859H18.84L15.46 2.19859C15.19 1.92859 14.75 1.92859 14.47 2.19859C14.2 2.46859 14.2 2.90859 14.47 3.18859L16.86 5.57859H7.14L9.53 3.18859C9.8 2.91859 9.8 2.47859 9.53 2.19859C9.26 1.92859 8.82 1.92859 8.54 2.19859L5.17 5.57859H4.77C3.87 5.57859 2 5.57859 2 8.13859C2 9.10859 2.2 9.74859 2.62 10.1686C2.86 10.4186 3.15 10.5486 3.46 10.6186C3.75 10.6886 4.06 10.6986 4.36 10.6986H19.64C19.95 10.6986 20.24 10.6786 20.52 10.6186C21.36 10.4186 22 9.81859 22 8.13859C22 5.57859 20.13 5.57859 19.24 5.57859Z" fill="currentColor"/>
                  <path d="M19.0897 12H4.90971C4.28971 12 3.81971 12.55 3.91971 13.16L4.75971 18.3C5.03971 20.02 5.78971 22 9.11971 22H14.7297C18.0997 22 18.6997 20.31 19.0597 18.42L20.0697 13.19C20.1897 12.57 19.7197 12 19.0897 12ZM14.8797 16.05L11.6297 19.05C11.4897 19.18 11.3097 19.25 11.1197 19.25C10.9297 19.25 10.7397 19.18 10.5897 19.03L9.08971 17.53C8.79971 17.24 8.79971 16.76 9.08971 16.47C9.38971 16.18 9.85971 16.18 10.1597 16.47L11.1497 17.46L13.8697 14.95C14.1697 14.67 14.6497 14.69 14.9297 14.99C15.2097 15.3 15.1897 15.77 14.8797 16.05Z" fill="currentColor"/>
                </svg>
              }
            </div>
            <h1 className="font-light text-xl sm:text-2lg md:text-2xl text-center leading-tight mt-3.75">
              How would you like <br />
              <span className="font-bold">to complete your Order?</span>
            </h1>
          </div>
          <div className="mt-6.25 divide-y divide-grey-border divide-opacity-30 rounded-20 overflow-hidden w-full">
            {showPayments && (
              <CardWithDetails
                icon={
                  <figure className="rounded-full w-8 h-8 sm:h-10 sm:w-10 text-accent-orange-500 bg-white shadow-card flex items-center justify-center">
                    {/* prettier-ignore */}
                    <svg width="55%" viewBox="0 0 24 24" fill="none">
                      <path d="M19 15C16.79 15 15 16.79 15 19C15 19.75 15.21 20.46 15.58 21.06C16.27 22.22 17.54 23 19 23C20.46 23 21.73 22.22 22.42 21.06C22.79 20.46 23 19.75 23 19C23 16.79 21.21 15 19 15ZM21.07 18.57L18.94 20.54C18.8 20.67 18.61 20.74 18.43 20.74C18.24 20.74 18.05 20.67 17.9 20.52L16.91 19.53C16.62 19.24 16.62 18.76 16.91 18.47C17.2 18.18 17.68 18.18 17.97 18.47L18.45 18.95L20.05 17.47C20.35 17.19 20.83 17.21 21.11 17.51C21.39 17.81 21.37 18.28 21.07 18.57Z" fill="currentColor"/>
                      <path d="M22 7.54844V7.99844C22 8.54844 21.55 8.99844 21 8.99844H3C2.45 8.99844 2 8.54844 2 7.99844V7.53844C2 5.24844 3.85 3.39844 6.14 3.39844H17.85C20.14 3.39844 22 5.25844 22 7.54844Z" fill="currentColor"/>
                      <path d="M2 11.4983V16.4583C2 18.7483 3.85 20.5983 6.14 20.5983H12.4C12.98 20.5983 13.48 20.1083 13.43 19.5283C13.29 17.9983 13.78 16.3383 15.14 15.0183C15.7 14.4683 16.39 14.0483 17.14 13.8083C18.39 13.4083 19.6 13.4583 20.67 13.8183C21.32 14.0383 22 13.5683 22 12.8783V11.4883C22 10.9383 21.55 10.4883 21 10.4883H3C2.45 10.4983 2 10.9483 2 11.4983ZM8 17.2483H6C5.59 17.2483 5.25 16.9083 5.25 16.4983C5.25 16.0883 5.59 15.7483 6 15.7483H8C8.41 15.7483 8.75 16.0883 8.75 16.4983C8.75 16.9083 8.41 17.2483 8 17.2483Z" fill="currentColor"/>
                    </svg>
                  </figure>
                }
                chosen={orderCompletionMethod}
                title="Pay Now"
                description={
                  payementsEnabled ? paymentCopy[order?.currency] : "Seller has not enabled payments for this store"
                }
                value="pay-now"
                name="completion-method"
                onChange={() => setOrderCompletionMethod("pay-now")}
                disabled={!payementsEnabled}
              />
            )}
            <CardWithDetails
              icon={
                <figure className="rounded-full w-8 h-8 sm:h-10 sm:w-10 text-accent-green-500 bg-white shadow-card flex items-center justify-center">
                  {/* prettier-ignore */}
                  <svg className="w-[55%]" viewBox="0 0 20 20" fill="none">
                    <path d="M18.3172 9.50829C18.0339 4.67496 13.6422 0.949968 8.5839 1.7833C5.10056 2.3583 2.3089 5.18328 1.76724 8.66662C1.45057 10.6833 1.86725 12.5916 2.77559 14.1666L2.03391 16.925C1.86724 17.55 2.44223 18.1166 3.05889 17.9416L5.77557 17.1916C7.0089 17.9166 8.45057 18.3333 9.99223 18.3333C14.6922 18.3333 18.5922 14.1916 18.3172 9.50829ZM14.0672 13.1C13.9922 13.25 13.9006 13.3916 13.7839 13.525C13.5756 13.75 13.3505 13.9166 13.1005 14.0166C12.8505 14.125 12.5756 14.175 12.2839 14.175C11.8589 14.175 11.4006 14.075 10.9256 13.8666C10.4422 13.6583 9.96725 13.3833 9.49225 13.0416C9.00892 12.6916 8.55891 12.3 8.12557 11.875C7.69224 11.4416 7.30889 10.9833 6.95889 10.5083C6.61722 10.0333 6.34224 9.55828 6.14224 9.08328C5.94224 8.60828 5.84225 8.14996 5.84225 7.71663C5.84225 7.4333 5.89224 7.1583 5.99224 6.9083C6.09224 6.64996 6.25059 6.41663 6.47559 6.2083C6.74226 5.94163 7.0339 5.81663 7.34224 5.81663C7.4589 5.81663 7.57555 5.84163 7.68388 5.89163C7.79222 5.94163 7.89224 6.01663 7.96724 6.12496L8.93388 7.49161C9.00888 7.59995 9.06723 7.69162 9.10056 7.78329C9.14223 7.87495 9.1589 7.95828 9.1589 8.04162C9.1589 8.14162 9.12558 8.24163 9.06724 8.34163C9.00891 8.44163 8.93389 8.54162 8.83389 8.64162L8.51722 8.97495C8.46722 9.02495 8.45059 9.07496 8.45059 9.14162C8.45059 9.17496 8.45888 9.20829 8.46722 9.24162C8.48388 9.27495 8.49225 9.29996 8.50058 9.32496C8.57558 9.46662 8.70889 9.64161 8.89223 9.85828C9.08389 10.0749 9.28393 10.3 9.50059 10.5166C9.72559 10.7416 9.94225 10.9416 10.1672 11.1333C10.3839 11.3166 10.5672 11.4416 10.7089 11.5166C10.7339 11.525 10.7589 11.5416 10.7839 11.5499C10.8172 11.5666 10.8506 11.5666 10.8922 11.5666C10.9672 11.5666 11.0173 11.5416 11.0673 11.4916L11.3839 11.175C11.4922 11.0666 11.5923 10.9916 11.6839 10.9416C11.7839 10.8833 11.8756 10.8499 11.9839 10.8499C12.0672 10.8499 12.1506 10.8666 12.2422 10.9083C12.3339 10.95 12.4339 11 12.5339 11.075L13.9172 12.0583C14.0256 12.1333 14.1006 12.225 14.1506 12.325C14.1922 12.4333 14.2172 12.5333 14.2172 12.65C14.1672 12.7916 14.1339 12.95 14.0672 13.1Z" fill="currentColor"/>
                  </svg>
                </figure>
              }
              chosen={orderCompletionMethod}
              disabled={disableWhatsappCompletion}
              title="Send to WhatsApp"
              description={
                disableWhatsappCompletion
                  ? "Order can be sent to whatsapp after payment"
                  : "Finalize order with merchant on Whatsapp"
              }
              value="whatsapp"
              onChange={() => {
                setOrderCompletionMethod("whatsapp");
              }}
              name="completion-method"
              showDetails={whatsappOptions.length > 1}
            >
              <div className="space-y-2.5">
                {whatsappOptions.map((c, index) => (
                  <div key={c.id} className="flex items-center">
                    <Radio
                      className="h-fit"
                      name="complete_order"
                      value={c}
                      chosen={checkoutOption}
                      onChange={() => setCheckoutOption(c)}
                      checked={checkoutOption?.id === c.id}
                      small
                      innerChild={
                        <span className="font-medium text-black-secondary text-1xs sm:text-sm ml-2.5">
                          {c.label} <span className="font-normal text-black-muted">{resolvePhone(c.phone)} </span>
                        </span>
                      }
                    />
                  </div>
                ))}
              </div>
            </CardWithDetails>

            {showInstagramOption && (
              <CardWithDetails
                icon={
                  <figure className="rounded-full w-8 h-8 sm:h-10 sm:w-10 text-accent-red-500 bg-white shadow-card flex items-center justify-center">
                    {/* prettier-ignore */}
                    <svg className="w-[55%]" viewBox="0 0 24 24" fill="none">
                      <path d="M16.19 2H7.81C4.17 2 2 4.17 2 7.81V16.18C2 19.83 4.17 22 7.81 22H16.18C19.82 22 21.99 19.83 21.99 16.19V7.81C22 4.17 19.83 2 16.19 2ZM12 15.88C9.86 15.88 8.12 14.14 8.12 12C8.12 9.86 9.86 8.12 12 8.12C14.14 8.12 15.88 9.86 15.88 12C15.88 14.14 14.14 15.88 12 15.88ZM17.92 6.88C17.87 7 17.8 7.11 17.71 7.21C17.61 7.3 17.5 7.37 17.38 7.42C17.26 7.47 17.13 7.5 17 7.5C16.73 7.5 16.48 7.4 16.29 7.21C16.2 7.11 16.13 7 16.08 6.88C16.03 6.76 16 6.63 16 6.5C16 6.37 16.03 6.24 16.08 6.12C16.13 5.99 16.2 5.89 16.29 5.79C16.52 5.56 16.87 5.45 17.19 5.52C17.26 5.53 17.32 5.55 17.38 5.58C17.44 5.6 17.5 5.63 17.56 5.67C17.61 5.7 17.66 5.75 17.71 5.79C17.8 5.89 17.87 5.99 17.92 6.12C17.97 6.24 18 6.37 18 6.5C18 6.63 17.97 6.76 17.92 6.88Z" fill="currentColor"/>
                    </svg>
                  </figure>
                }
                chosen={orderCompletionMethod}
                title="Send to Instagram"
                description="Finalize order with merchant on Instagram"
                value="instagram"
                onChange={() => {
                  setOrderCompletionMethod("instagram");
                  const orderMessage = getOrderMessage(order, "Hi, i'll like to buy these items:", account, null, true);
                  copy(orderMessage);
                }}
                name="completion-method"
                showDetails={orderCompletionMethod === "instagram"}
              >
                <div className="text-1xs text-accent-orange-500 !leading-snug font-medium">
                  Order text copied. Click &apos;Complete Order&apos; to open the merchant&apos;s DM and paste text
                  there.
                </div>
              </CardWithDetails>
            )}
          </div>
          {/* 
          {isStoreOwner && (
            <CardWithDetails
              icon={
                <figure className="rounded-full w-8 h-8 sm:h-10 sm:w-10 bg-accent-yellow-500 text-white flex items-center justify-center">
                  prettier-ignore
                  <svg className="w-[55%]" viewBox="0 0 24 24" fill="none">
                    <path d="M19.24 5.57859H18.84L15.46 2.19859C15.19 1.92859 14.75 1.92859 14.47 2.19859C14.2 2.46859 14.2 2.90859 14.47 3.18859L16.86 5.57859H7.14L9.53 3.18859C9.8 2.91859 9.8 2.47859 9.53 2.19859C9.26 1.92859 8.82 1.92859 8.54 2.19859L5.17 5.57859H4.77C3.87 5.57859 2 5.57859 2 8.13859C2 9.10859 2.2 9.74859 2.62 10.1686C2.86 10.4186 3.15 10.5486 3.46 10.6186C3.75 10.6886 4.06 10.6986 4.36 10.6986H19.64C19.95 10.6986 20.24 10.6786 20.52 10.6186C21.36 10.4186 22 9.81859 22 8.13859C22 5.57859 20.13 5.57859 19.24 5.57859Z" fill="currentColor"/>
                    <path d="M19.0506 12H4.87064C4.25064 12 3.78064 12.55 3.88064 13.16L4.72064 18.3C5.00064 20.02 5.75064 22 9.08064 22H14.6906C18.0606 22 18.6606 20.31 19.0206 18.42L20.0306 13.19C20.1506 12.57 19.6806 12 19.0506 12ZM12.0006 19.5C9.66064 19.5 7.75064 17.59 7.75064 15.25C7.75064 14.84 8.09064 14.5 8.50064 14.5C8.91064 14.5 9.25064 14.84 9.25064 15.25C9.25064 16.77 10.4806 18 12.0006 18C13.5206 18 14.7506 16.77 14.7506 15.25C14.7506 14.84 15.0906 14.5 15.5006 14.5C15.9106 14.5 16.2506 14.84 16.2506 15.25C16.2506 17.59 14.3406 19.5 12.0006 19.5Z" fill="currentColor"/>
                  </svg>
                </figure>
              }
              className="mt-6.25"
              chosen={orderCompletionMethod}
              title="Open Order Page"
              description="Open order page to confirm & generate receipt"
              value="order-page"
              onChange={() => setOrderCompletionMethod("order-page")}
              name="completion-method"
              showDetails={whatsapp.length > 1}
            ></CardWithDetails>
          )} */}

        </div>
      </ModalBody>
      <ModalFooter>
        <div className="w-full">
          <div className="flex items-center gap-x-3">
            <AppBtn className="flex-1" color="neutral" onClick={prevStep} size="lg">
              Cancel
            </AppBtn>
            <AppBtn className="flex-1" disabled={!submitIsEnabled()} onClick={() => complete()} size="lg">
              {submitButtonLabel(orderCompletionMethod)}
            </AppBtn>
          </div>
        </div>
      </ModalFooter>
    </>
  );
};

const submitButtonLabel = (method: string) => {
  switch (method) {
    case "pay-now":
      return "Make Payment";
    case "whatsapp":
      return "Send to Whatsapp";
    default:
      return "Complete Order";
  }
};

const paymentCopy = {
  [CURRENCIES.GHC]: "Pay with MoMo or Card",
  [CURRENCIES.NGN]: "Pay with any method of your choice",
  [CURRENCIES.KES]: "Pay with M-Pesa or Card",
  [CURRENCIES.ZAR]: "Pay with Card or EFT",
  [CURRENCIES.USD]: "Pay with Card or Wallets",
  [CURRENCIES.GBP]: "Pay with Card or Bank",
  [CURRENCIES.CAD]: "Pay with Interac",
};

export default FinalizeOrder;
